import { useState } from 'react';
import { probetrainingAPI } from '@/lib/api';
import { toast } from 'sonner';
import { CheckCircle2, XCircle, Handshake, Hourglass, ThumbsDown, BadgePercent } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/* EIN Baustein für „Erschienen“, „Nicht da“ und das Ergebnis (Vertrag · Bedenkzeit mit Tag-1-Frage · kein Interesse) —
   Probetrainings-Liste, Lead-Ansicht und Closer benutzen ihn (Betreiber 30.10.2026: ein Weg je Vorgang). Gespeichert wird
   immer über denselben Server-Weg (probetrainingAPI.status / .leadStatus / .ergebnis); die Auswertungen lesen es dort. */

/* Sales Tracking (24.09.2026): nach "erschienen" ist das Ergebnis Pflicht —
   Abschluss setzt der Verkaufsrechner selbst (Vertrag), hier nur Bedenkzeit
   und "kein Interesse" mit Verlustgrund. */
const VERLUSTGRUENDE = [
  ['preis', 'Preis'], ['zeit', 'Zeit'], ['partner_fragen', 'Partner fragen'], ['ort', 'Ort'],
  ['angebot_passt_nicht', 'Angebot passt nicht'], ['sonstiges', 'Sonstiges'],
];
export const ERGEBNIS_LABEL = { abschluss: 'Abschluss', bedenkzeit: 'Bedenkzeit', kein_interesse: 'Kein Interesse' };

/* Verkaufsrechner mit dem Lead: die Lead-ID genügt, der Rechner lädt den Rest (Name, Kontakt, Adresse, Tag-1-Angebot). */
export function verkaufsLink(lead) {
  const p = new URLSearchParams({ lead: lead.id || '', name: lead.name || '', telefon: lead.phone || '', email: lead.email || '' });
  return `/verkauf?${p.toString()}`;
}

export function ErgebnisWahl({ pt, onFertig, navigate }) {
  const [offen, setOffen] = useState(false);
  const [bedenk, setBedenk] = useState(false);     // Tag-1-Frage (24.10.2026)
  const [grund, setGrund] = useState('');
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const buchungId = pt.probetraining?.buchung_id;

  const senden = async (daten) => {
    setBusy(true);
    try {
      // Probetraining nur am Lead (ohne Buchung): Ergebnis am Lead (29.09.2026)
      if (buchungId) await probetrainingAPI.ergebnis(buchungId, daten);
      else await probetrainingAPI.leadErgebnis(pt.id, daten);
      toast.success(daten.ergebnis === 'bedenkzeit' ? (daten.tag1 ? 'Bedenkzeit vermerkt · Erinnerung für morgen früh angelegt' : 'Bedenkzeit vermerkt') : 'Als „kein Interesse" vermerkt — danke für den Grund');
      setOffen(false);
      onFertig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konnte nicht gespeichert werden');
    }
    setBusy(false);
  };

  return (
    <span className="flex flex-col items-end gap-1 max-w-full" onClick={(e) => e.stopPropagation()} data-testid={`pt-ergebnis-${pt.id}`}>
      {/* umbrechen statt überstehen: in der schmalen Details-Karte der Lead-Ansicht ragte „Vertrag“ über den Rand (31.10.2026) */}
      <span className="flex flex-wrap justify-end gap-1">
        <Button size="sm" className="h-7 gap-1 bg-marke hover:bg-marke-dunkel" onClick={() => navigate(verkaufsLink(pt))} title="Vertrag im Verkaufsrechner — setzt das Ergebnis automatisch">
          <Handshake className="w-3.5 h-3.5" /> Vertrag
        </Button>
        <Button size="sm" variant="outline" className="h-7 gap-1" disabled={busy} onClick={() => { setBedenk(!bedenk); setOffen(false); }} data-testid="pt-bedenkzeit">
          <Hourglass className="w-3.5 h-3.5" /> Bedenkzeit
        </Button>
        <Button size="sm" variant="ghost" className="h-7 gap-1 text-muted-foreground" disabled={busy} onClick={() => { setOffen(!offen); setBedenk(false); }}>
          <ThumbsDown className="w-3.5 h-3.5" /> Kein Interesse
        </Button>
      </span>
      {/* Tag-1-Angebot (Wunsch Cagla, 24.10.2026): mit Tag 1 beraten -> Tag-1-Preis gilt 7 Tage, Aufgabe am Morgen danach */}
      {bedenk && (
        <span className="flex flex-wrap gap-1 items-center justify-end text-xs" data-testid="pt-tag1-frage">
          <span className="text-muted-foreground">Mit dem Tag-1-Angebot beraten?</span>
          <Button size="sm" className="h-7" disabled={busy} onClick={() => senden({ ergebnis: 'bedenkzeit', tag1: true })} data-testid="pt-tag1-ja"
                  title="Tag-1-Preis gilt noch 7 Tage; morgen früh steht die Aufgabe „Tag-1-Angebot nachfassen“ in der Arbeitsliste">Ja, erinnern</Button>
          <Button size="sm" variant="outline" className="h-7" disabled={busy} onClick={() => senden({ ergebnis: 'bedenkzeit' })} data-testid="pt-tag1-nein">Nein</Button>
        </span>
      )}
      {offen && (
        <span className="flex flex-wrap gap-1 items-center justify-end">
          <select value={grund} onChange={(e) => setGrund(e.target.value)} className="h-7 rounded-md border border-border bg-background px-2 text-xs" data-testid="pt-verlustgrund">
            <option value="">Grund (Pflicht)…</option>
            {VERLUSTGRUENDE.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {grund === 'sonstiges' && (
            <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Grund in Worten" className="h-7 w-40 text-xs" />
          )}
          <Button size="sm" variant="outline" className="h-7" disabled={busy || !grund || (grund === 'sonstiges' && !text.trim())}
                  onClick={() => senden({ ergebnis: 'kein_interesse', verlustgrund: grund, verlustgrund_text: text })}>
            Speichern
          </Button>
        </span>
      )}
    </span>
  );
}


/* Da / Nicht da: mit Buchung über die Buchung, sonst über den Lead — der Server nimmt in beiden Fällen dieselbe Funktion. */
export function StatusKnoepfe({ pt, onFertig, className = '' }) {
  const [busy, setBusy] = useState(false);
  const buchungId = pt.probetraining?.buchung_id;
  const setzen = async (status) => {
    setBusy(true);
    try {
      if (buchungId) await probetrainingAPI.status(buchungId, status);
      else await probetrainingAPI.leadStatus(pt.id, status);
      toast.success(status === 'erschienen' ? 'Als erschienen vermerkt' : 'Als nicht erschienen vermerkt');
      onFertig && onFertig(status);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konnte nicht gespeichert werden');
    }
    setBusy(false);
  };
  return (
    <span className={`flex gap-1 flex-shrink-0 ${className}`} onClick={(e) => e.stopPropagation()} data-testid={`pt-status-${pt.id}`}>
      <Button size="sm" variant="outline" className="h-7 gap-1" disabled={busy} onClick={() => setzen('erschienen')} data-testid="pt-da">
        <CheckCircle2 className="w-3.5 h-3.5" /> Da
      </Button>
      <Button size="sm" variant="ghost" className="h-7 gap-1 text-muted-foreground" disabled={busy} onClick={() => setzen('nicht_erschienen')} data-testid="pt-nicht-da">
        <XCircle className="w-3.5 h-3.5" /> Nicht da
      </Button>
    </span>
  );
}

/* Ganzer Baustein für eine Zeile: je nach Stand Da/Nicht da oder (nach „erschienen“) das Ergebnis. */
export default function ProbetrainingErgebnis({ pt, onFertig, navigate, nurFaellige = true }) {
  const status = pt.termin_status;
  const erschienen = pt.probetraining?.status === 'erschienen' || status === 'besucht';
  const hatErgebnis = !!pt.probetraining?.ergebnis || !!pt.abschluss;
  if (status === 'geplant' && (!nurFaellige || pt.faellig !== false)) return <StatusKnoepfe pt={pt} onFertig={onFertig} />;
  if (erschienen && !hatErgebnis) return <ErgebnisWahl pt={pt} onFertig={onFertig} navigate={navigate} />;
  return null;
}


/* Tag-1-Angebot von Hand (Betreiber 02.11.2026: „auch trotz verpasstem Termin, wenn wir es manuell erfassen, noch
   nachfassen“): Grund Pflicht, gilt ab heute 7 Tage, die Aufgabe „Tag-1-Angebot nachfassen“ ist sofort fällig. */
export function Tag1Manuell({ pt, onFertig }) {
  const [offen, setOffen] = useState(false);
  const [grund, setGrund] = useState('');
  const [busy, setBusy] = useState(false);
  const speichern = async () => {
    setBusy(true);
    try {
      const { data } = await probetrainingAPI.tag1Manuell(pt.id, grund.trim());
      const bis = data?.tag1_angebot?.bis || '';
      toast.success(`Tag-1-Angebot vermerkt, gilt bis ${bis.slice(8, 10)}.${bis.slice(5, 7)}. · Aufgabe zum Nachfassen angelegt`);
      setOffen(false); setGrund('');
      onFertig && onFertig();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Konnte nicht gespeichert werden');
    }
    setBusy(false);
  };
  return (
    <span className="flex flex-col items-end gap-1 flex-shrink-0" onClick={(e) => e.stopPropagation()} data-testid={`pt-tag1-manuell-${pt.id}`}>
      <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-violet-700" onClick={() => setOffen(!offen)}
              title="Tag-1-Preis (Betreuter Einstieg 99 statt 149 €) ab heute 7 Tage freigeben und nachfassen — auch nach verpasstem Termin">
        <BadgePercent className="w-3.5 h-3.5" /> Tag 1 nachträglich
      </Button>
      {offen && (
        <span className="flex flex-wrap gap-1 items-center justify-end">
          <Input value={grund} onChange={(e) => setGrund(e.target.value)} placeholder="Grund (Pflicht)" className="h-7 w-48 text-xs" maxLength={300} />
          <Button size="sm" variant="outline" className="h-7" disabled={busy || grund.trim().length < 5} onClick={speichern}>Vermerken</Button>
        </span>
      )}
    </span>
  );
}
